<template>
  <div class="flex flex-col bg-gray-900 terminal-font" style="height: calc(100vh - 65px)">
    <!-- Page header -->
    <div class="flex items-center gap-3 px-4 pt-4 pb-2">
      <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
        <span class="material-icons-round">receipt_long</span>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-100">Activity Logs</h2>
        <p class="text-sm text-gray-400">Track admin actions across the system.</p>
      </div>
    </div>
    <!-- Filter bar -->
    <div class="flex flex-wrap items-end gap-4 px-4 py-3 text-xs bg-gray-800 border-b border-gray-700">
      <div>
        <label class="block mb-1 text-gray-400">--type</label>
        <select
          v-model="selectedFilter"
          class="px-2 py-1.5 text-gray-200 bg-gray-900 border border-gray-600 rounded focus:outline-none focus:border-green-500"
        >
          <option :selected="selectedFilter === 'all'" value="all">all</option>
          <option
            v-for="type in activityLogTypes"
            :key="`activity-type-${type}`"
            :selected="selectedFilter === type"
            :value="type"
          >
            {{ type }}
          </option>
        </select>
      </div>

      <div>
        <label class="block mb-1 text-gray-400">--actor</label>
        <select
          v-model="selectedActorType"
          class="px-2 py-1.5 text-gray-200 bg-gray-900 border border-gray-600 rounded focus:outline-none focus:border-green-500"
        >
          <option :selected="selectedActorType === 'all'" value="all">all</option>
          <option
            v-for="actorType in [{value: 'accounts', label: 'Account'}, { value: 'Drivers', label: 'Drivers'}, { value: 'Users', label: 'Passengers'} ]"
            :key="`actor-type-${actorType.value}`"
            :selected="selectedActorType === actorType.value"
            :value="actorType.value"
          >
            {{ actorType.label }}
          </option>
        </select>
      </div>

      <div class="relative" v-if="selectedActorType === 'Drivers'">
        <label class="block mb-1 text-gray-400">--driver</label>
        <input
          v-model="selectedDriver"
          @keyup="searchDriver"
          class="w-56 px-2 py-1.5 text-gray-200 placeholder-gray-500 bg-gray-900 border border-gray-600 rounded focus:outline-none focus:border-green-500"
          placeholder="search drivers..."
        />
        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <div
            v-if="driverSearchResults.length"
            class="absolute inset-x-0 z-50 mt-1"
          >
            <div
              class="py-2 overflow-y-auto bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-64"
            >
              <ul>
                <li
                  v-for="(result, index) in driverSearchResults"
                  :key="result._id"
                  @click="selectDriver(index)"
                  class="flex items-center px-4 py-2 space-x-2 border-t border-gray-700 cursor-pointer hover:bg-gray-700"
                >
                  <img
                    class="object-cover w-10 h-10 rounded-full"
                    :src="result.profileImage"
                    :alt="`${result.firstName} ${result.lastName}`"
                  />
                  <div>
                    <p class="text-gray-200">{{ `${result.firstName} ${result.lastName}` }}</p>
                    <p class="text-xs font-semibold text-gray-400">
                      {{ result.phoneNumber }}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </transition>
      </div>

      <div class="relative" v-else-if="selectedActorType === 'Users'">
        <label class="block mb-1 text-gray-400">--passenger</label>
        <input
          v-model="selectedPassenger"
          @keyup="searchPassenger"
          class="w-56 px-2 py-1.5 text-gray-200 placeholder-gray-500 bg-gray-900 border border-gray-600 rounded focus:outline-none focus:border-green-500"
          placeholder="search passengers..."
        />
        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <div
            v-if="passengerSearchResults.length"
            class="absolute inset-x-0 z-50 mt-1"
          >
            <div
              class="py-2 overflow-y-auto bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-64"
            >
              <ul>
                <li
                  v-for="(result, index) in passengerSearchResults"
                  :key="result._id"
                  @click="selectPassenger(index)"
                  class="flex items-center px-4 py-2 space-x-2 border-t border-gray-700 cursor-pointer hover:bg-gray-700"
                >
                  <div>
                    <p class="text-gray-200">{{ `${result.firstName} ${result.lastName}` }}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </transition>
      </div>

      <div class="relative" v-else-if="selectedActorType === 'accounts'">
        <label class="block mb-1 text-gray-400">--user</label>
        <input
          v-model="selectedUser"
          @keyup="searchUser"
          class="w-56 px-2 py-1.5 text-gray-200 placeholder-gray-500 bg-gray-900 border border-gray-600 rounded focus:outline-none focus:border-green-500"
          placeholder="search users..."
        />
        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <div
            v-if="userSearchResults.length"
            class="absolute inset-x-0 z-50 mt-1"
          >
            <div
              class="py-2 overflow-y-auto bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-64"
            >
              <ul>
                <li
                  v-for="(result, index) in userSearchResults"
                  :key="result._id"
                  @click="selectUser(index)"
                  class="flex items-center px-4 py-2 space-x-2 border-t border-gray-700 cursor-pointer hover:bg-gray-700"
                >
                  <div>
                    <p class="text-gray-200">{{ `${result.firstName} ${result.lastName}` }}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </transition>
      </div>

      <div>
        <label class="block mb-1 text-gray-400">--from / --to</label>
        <v-date-picker v-model="range" is-range>
          <template v-slot="{ inputValue, inputEvents }">
            <div class="flex items-center">
              <input
                :value="inputValue.start"
                v-on="inputEvents.start"
                placeholder="from date"
                class="w-28 px-2 py-1.5 text-gray-200 placeholder-gray-500 bg-gray-900 border border-gray-600 rounded focus:outline-none focus:border-green-500"
              />
              <span class="mx-2 text-gray-500">→</span>
              <input
                :value="inputValue.end"
                v-on="inputEvents.end"
                placeholder="to date"
                class="w-28 px-2 py-1.5 text-gray-200 placeholder-gray-500 bg-gray-900 border border-gray-600 rounded focus:outline-none focus:border-green-500"
              />
            </div>
          </template>
        </v-date-picker>
      </div>
    </div>

    <!-- Terminal body -->
    <div class="flex-1 p-4 overflow-y-auto text-sm terminal-body">
      <!-- Command line -->
      <div class="mb-3 whitespace-nowrap">
        <span class="text-green-400">admin@taxiye</span><span class="text-gray-500">:</span><span class="text-blue-400">~/var/log</span><span class="text-gray-500">$</span>
        <span class="text-gray-200"> tail -n {{ perPage }} --page={{ currentPage }}<template v-if="selectedFilter !== 'all'"> --type={{ selectedFilter }}</template><template v-if="selectedActorType !== 'all'"> --actor={{ selectedActorType }}</template> activity.log</span>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-gray-400">
        <span class="text-yellow-400">⣾</span> fetching activity logs<span class="dots"></span>
      </div>

      <!-- Log lines -->
      <template v-else-if="activityLogs.length">
        <div
          v-for="(activityLog, index) in activityLogs"
          :key="activityLog._id"
          class="py-0.5 leading-relaxed whitespace-pre-wrap break-words border-b border-gray-800/60 hover:bg-gray-800/50"
        >
          <span class="text-gray-500 select-none">{{ lineNumber(index) }}</span>
          <span class="text-teal-400">[{{ formatDate(activityLog.createdAt) }}]</span>
          <span class="font-semibold text-yellow-400">{{ activityLog.subjectModel }}<template v-if="activityLog.subject">/{{ activityLog.subject }}</template></span>
          <span class="text-gray-200"> {{ activityLog.action }}</span>
          <template v-if="activityLog.url">
            <span class="text-gray-500"> →</span>
            <span class="text-blue-400"> {{ activityLog.url }}</span>
          </template>
          <template v-else-if="activityLog.resource">
            <span class="text-gray-500"> →</span>
            <span class="text-purple-400"> {{ activityLog.resourceModel }} - {{ activityLog.resource }}</span>
          </template>
        </div>
      </template>

      <!-- Empty -->
      <div v-else class="text-gray-400">
        <span class="text-red-400">✗</span> no activity logs found — 0 lines matched
      </div>
    </div>

    <!-- Pagination footer -->
    <div class="px-4 py-3 bg-gray-800 border-t border-gray-700">
      <pagination
        class="flex space-x-2"
        :currentPage="currentPage"
        :total="total"
        @goto="goToPage"
        @setPerPage="setPerPage"
        :perPage="perPage"
        :start="start"
        :end="end"
        :count="paginator.count"
      ></pagination>
    </div>
  </div>
</template>

<script>
// import moment from "moment";
import PaginationMixin from "@/mixins/Pagination";
// import ExportButton from "@/components/ExportButton";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      activityLogs: [],
      openDropdown: null,
      loading: true,
      selectedFilter: "all",
      selectedActorType: "all",
  
      selectedDriver: "",

      driverSearchResults: [],
      userSearchResults: [],
      passengerSearchResults: [],

      driverId: null,

      userId: null,

      passengerId: null,


      // logFilter: "",

      range: {
        start: null,
        end: null,
      },

      activityLogTypes: [],
    };
  },
  components: {
    // ExportButton,
    Pagination,
  },
  watch: {
    currentPage() {
      this.getActivityLogs();
    },
    selectedFilter() {
      this.getActivityLogs();
    },
    selectedActorType() {
      this.getActivityLogs();
    },

    driverId() {
      this.getActivityLogs();
    },
    userId() {
      this.getActivityLogs();
    },
    passengerId() {
      this.getActivityLogs();
    },

    // logFilter() {
    //   this.getActivityLogs();
    // },

    perPage() {
      this.currentPage === 1 ? this.getActivityLogs() : (this.currentPage = 1);
    },
    range: {
      handler() {
        this.getActivityLogs();
      },
      deep: true,
    },
    $route() {
      if (this.$route.query.status) {
        this.selectedFilter = this.$route.query.status;
      }
    },
  },
  created() {
    // var sd = new Date();
    // sd.setDate(0);
    // sd.setDate(1);
    // var ed = new Date();
    // ed.setMonth(ed.getMonth() + 1);
    // ed.setDate(0);
    // this.range.start = sd;
    // this.range.end = ed;

this.$misc.get('/activity-logs/types').then(activityLogTypes => {
  this.activityLogTypes = activityLogTypes.data
})

this.perPage = 50
    if (this.$route.query.status) {
      this.selectedFilter = this.$route.query.status;
    }
    if (this.$route.query.page) {
      this.currentPage = this.$route.query.page;
    }
    if (this.$route.query.passenger) {
      this.passengerId = this.$route.query.passenger;
      this.$misc
        .get("users/" + this.$route.query.passenger)
        .then(({ data: { firstName, lastName, phoneNumber } }) => {
          this.selectedPassenger = `${firstName} ${lastName} - ${phoneNumber}`;
        });
    }
    if (this.$route.query.driver) {
      this.driverId = this.$route.query.driver;
      this.$misc.get("drivers/" + this.$route.query.driver).then(({ data }) => {
        const { firstName, lastName, phoneNumber } = data.driver;
        this.selectedDriver = `${firstName} ${lastName} - ${phoneNumber}`;
        this.selectedDriverObj = data.driver;
      });
    }

    this.getActivityLogs();
    this.$misc
      .get("/accounts")
      .then((response) => (this.accounts = response.data.data))
      .catch((err) => console.log(err));
  },
  computed: {
    driverFilter() {
      return this.$route.query.driver || null;
    },
    riderFilter() {
      return this.$route.query.passenger || null;
    },
    reportDownloadUrl() {
      return (
        "bans/export?" +
        Object.entries({
          ...Object.fromEntries(
            Object.entries({
              driver: this.driverId ? this.driverId : undefined,
              dispatcher: this.userId ? this.userId : undefined,
              passenger: this.passengerId ? this.passengerId : undefined,
              status:
                this.selectedFilter && this.selectedFilter != "all"
                  ? this.selectedFilter
                  : undefined,
              ...(this.range || {}),
            }).filter(([key, val]) => key && val)
          ),
        })
          .map(([key, value]) => [key, value].join("="))
          .join("&")
      );
    },
  },
  methods: {
    lineNumber(index) {
      const line = (this.currentPage - 1) * this.perPage + index + 1;
      return String(line).padStart(4, " ");
    },

     searchDriver(e) {
      if (e.target.value.trim() === "") {
        this.driverSearchResults = [];
        this.driverId = null;
        this.getRides();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.driverSearchResults = [];
          this.$misc
            .get("drivers/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.driverSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.driverSearchResults = [];
        }
      }
    },

    searchUser(e) {
      if (e.target.value.trim() === "") {
        this.userSearchResults = [];
        this.userId = null;
        this.getRides();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.userSearchResults = [];
          this.$misc
            .get("accounts/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.userSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.userSearchResults = [];
        }
      }
    },

    searchPassenger(e) {
      if (e.target.value.trim() === "") {
        this.passengerSearchResults = [];
        this.passengerId = null;
        this.getRides();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.passengerSearchResults = [];
          this.$misc
            .get("users/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.passengerSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.passengerSearchResults = [];
        }
      }
    },

    selectDriver(index) {
      let driver = this.driverSearchResults[index];
      this.selectedDriverObj = driver;
      this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.driverId = driver._id;
      this.driverSearchResults = [];
      const query = { ...this.$route.query, driver: this.driverId };
      this.$router.push({ query });
      this.getRides();
    },

    selectUser(index) {
      let user = this.userSearchResults[index];
      this.selectedUserObj = user;
      this.selectedUser = `${user.firstName} ${user.lastName}`;
      this.userId = user._id;
      this.userSearchResults = [];
      this.getRides();
    },

    selectPassenger(index) {
      let user = this.passengerSearchResults[index];
      this.selectedPassengerObj = user;
      this.selectedPassenger = `${user.firstName} ${user.lastName} - ${user.phoneNumber}`;
      this.passengerId = user._id;
      this.passengerSearchResults = [];
      const query = { ...this.$route.query, passenger: this.passengerId };
      this.$router.push({ query });

      this.getRides();
    },




    deleteBan(banId) {
      this.$misc.delete(`bans/${banId}`).then(() => {
        this.getActivityLogs();
      });
    },

    formatDate(date) {
      // return moment(date).format("MMM D, YYYY HH:mm");
      return date ? date.replace("T", " ").slice(0, 16) : "";
    },
    toggleDropdown(index) {
      if (this.openDropdown === index) {
        this.openDropdown = false;
      } else {
        this.openDropdown = index;
      }
    },
    getActivityLogs() {
      this.loading = true;
      var path = `activity-logs?limit=${this.perPage}&page=${this.currentPage}`;

      if (this.selectedActorType != 'all') {
        path += "&actor=" + this.selectedActorType;
      }
      
      if (this.selectedActorType === 'Drivers' && this.driverId) {
        path += "&actorId=" + this.driverId;
      }

      if (this.selectedActorType === 'Users' && this.passengerId) {
        path += "&actorId=" + this.passengerId;
      }

      if (this.selectedActorType === 'accounts' && this.userId) {
        path += "&actorId=" + this.userId;
      }

      // if (this.logFilter) {
      //   path += "&log=" + this.logFilter
      // }
      
      if (this.selectedFilter && this.selectedFilter != "all") {
        path += "&actionCode=" + this.selectedFilter;
      }

      if (this.range && this.range.start && this.range.end) {
        path += "&from=" + this.range.start + "&to=" + this.range.end;
      }

      this.$misc
        .get(path)
        .then((response) => {
          this.activityLogs = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style scoped>
.terminal-font {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
}

.dots::after {
  content: "";
  animation: dots 1.2s steps(4, end) infinite;
}

@keyframes dots {
  0% { content: ""; }
  25% { content: "."; }
  50% { content: ".."; }
  75% { content: "..."; }
}

.terminal-body::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.terminal-body::-webkit-scrollbar-track {
  background: #111827;
}

.terminal-body::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 4px;
}
</style>
